import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { API_BASE_URL, getAuthHeaders } from './auth';
import AreDisclaimer from './AreDisclaimer';
import { EmailDraftCard, AttachmentCard } from './chatMessageStyles';
import ChartCard from './ChartCard';
import './AdvisorVoiceDialog.css';

/**
 * Speech-to-speech overlay for Assistant Bro MH (the EFD data agent).
 *
 * Turn-based, same shape as AdvisorVoiceDialog (shared `av-*` styling):
 * listen with voice-activity detection → POST the audio → stream
 * transcript / text / audio → play the audio queue → listen again.
 *
 * Two differences that matter for this agent:
 *  - the running chat history is sent along and every finished turn is handed
 *    back through `onTurn`, so a spoken conversation continues the written one
 *  - the backend may append a `screen` event: markdown (usually a table) that
 *    belongs on screen but is never spoken. It rides along with the turn.
 *  - likewise `email_draft` events: a drafted e-mail shown as a card (here in
 *    the transcript and, via `onTurn`, in the chat) — never spoken. `chart`
 *    events (create_chart) travel the same way.
 */

// Voice-activity-detection settings (same values as the ARE dialog).
const VAD_THRESHOLD = 0.025;
const MIN_SPEECH_MS = 400;
const SILENCE_MS = 1700;
const MAX_CONSECUTIVE_FAILURES = 3;
const NO_SPEECH_TIMEOUT_MS = 10000;  // no speech at all → end the turn
const MAX_RECORDING_MS = 30000;      // hard cap so the mic never hangs open

function EfdVoiceDialog({ apiBase = '/api/efd-agent', language = 'de', history = [], conversationId, onTurn, onClose }) {
  const { t } = useTranslation();
  const [voiceState, setVoiceState] = useState('idle');
  const [statusText, setStatusText] = useState('');
  const [responseText, setResponseText] = useState('');
  const [messages, setMessages] = useState([]);
  const [audioLevel, setAudioLevel] = useState(0);
  const transcriptRef = useRef(null);

  const mediaStreamRef = useRef(null);
  const analyserRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const vadFrameIdRef = useRef(null);
  const speechStartTimeRef = useRef(null);
  const silenceStartTimeRef = useRef(null);
  const listenStartTimeRef = useRef(null);
  const recordingTimeoutRef = useRef(null);
  const voiceStoppedByUserRef = useRef(false);
  const consecutiveFailuresRef = useRef(0);
  const abortControllerRef = useRef(null);

  // TTS playback queue
  const ttsAudioContextRef = useRef(null);
  const currentSourceRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const streamDoneRef = useRef(false);
  const botResponseRef = useRef('');
  const screenMarkdownRef = useRef('');
  const emailDraftsRef = useRef([]);   // drafts received during the current turn
  const attachmentsRef = useRef([]);   // files (theScreener PDFs) of the current turn
  const chartsRef = useRef([]);        // charts (create_chart) of the current turn
  const startListeningRef = useRef(null);
  // The finished answer waits here until it has been spoken — see finishTurn.
  const pendingTurnRef = useRef(null);

  // The conversation to send along. Kept in a ref and extended locally so the
  // second spoken turn already knows about the first without waiting for the
  // parent to re-render.
  const historyRef = useRef(history.map(({ role, content }) => ({ role, content })));
  // Fixed for this dialog: the chat's conversation (owner's Activity view).
  const conversationRef = useRef(conversationId);

  /** Hand the finished turn to the chat and remember it for the next one. */
  const commitTurn = useCallback((question, answer, screenMarkdown, emailDrafts = [], attachments = [], charts = []) => {
    if (!question && !answer && !emailDrafts.length && !attachments.length && !charts.length) return;
    // The chat shows the spoken answer plus, when present, the on-screen part.
    const content = [answer, screenMarkdown].filter(Boolean).join('\n\n');
    historyRef.current = [
      ...historyRef.current,
      ...(question ? [{ role: 'user', content: question }] : []),
      ...emailDrafts.map(d => ({
        role: 'assistant',
        content: `E-Mail-Entwurf an ${d.recipient_name || d.recipient_email || '?'}: ${d.subject || ''}`,
      })),
      ...attachments.map(a => ({
        role: 'assistant',
        content: `Dokument bereitgestellt: ${a.title || a.filename || ''}`,
      })),
      // The chart's compact text form (title + exact values) — what the
      // model reads back when asked to redraw it.
      ...charts.map(c => ({ role: 'assistant', content: c.summary || c.spec?.title || '' })),
      ...(content ? [{ role: 'assistant', content }] : []),
    ];
    if (onTurn) onTurn({ question, answer: content, emailDrafts, attachments, charts });
  }, [onTurn]);

  /** Close out a turn ONCE IT HAS BEEN SPOKEN: only now does the answer move
   *  from under the blob down into the transcript. Keeping it in place while
   *  the audio plays is the point — the text should stay where the user is
   *  looking for as long as they hear it. */
  const finishTurn = useCallback(() => {
    const turn = pendingTurnRef.current;
    pendingTurnRef.current = null;
    if (turn) {
      if (turn.answer) setMessages((prev) => [...prev, { role: 'assistant', text: turn.answer }]);
      commitTurn(turn.question, turn.answer, turn.screen, turn.emailDrafts || [], turn.attachments || [],
        turn.charts || []);
    }
    setResponseText('');
  }, [commitTurn]);

  const playNextInQueue = useCallback(() => {
    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      if (streamDoneRef.current) {
        finishTurn();
        setVoiceState('idle');
        if (!voiceStoppedByUserRef.current) {
          setTimeout(() => startListeningRef.current && startListeningRef.current(), 300);
        }
      }
      return;
    }

    isPlayingRef.current = true;
    const audioBase64 = audioQueueRef.current.shift();
    try {
      if (!ttsAudioContextRef.current || ttsAudioContextRef.current.state === 'closed') {
        ttsAudioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = ttsAudioContextRef.current;
      if (ctx.state === 'suspended') ctx.resume();

      const binaryString = atob(audioBase64);
      const arrayBuffer = new ArrayBuffer(binaryString.length);
      const bytes = new Uint8Array(arrayBuffer);
      for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);

      ctx.decodeAudioData(arrayBuffer).then((buffer) => {
        if (!ttsAudioContextRef.current || ttsAudioContextRef.current.state === 'closed') return;
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.onended = () => { currentSourceRef.current = null; playNextInQueue(); };
        currentSourceRef.current = source;
        source.start(0);
        setVoiceState('speaking');
      }).catch((err) => { console.error('Audio decode error:', err); playNextInQueue(); });
    } catch (err) {
      console.error('Playback error:', err);
      playNextInQueue();
    }
  }, [finishTurn]);

  const startListening = useCallback(() => {
    if (startListeningRef.current) startListeningRef.current();
  }, []);

  const sendVoiceToBackend = useCallback(async (audioBlob) => {
    // Hard-stop the previous turn before starting a new one, otherwise two
    // playback chains overlap and speak on top of each other.
    if (abortControllerRef.current) { try { abortControllerRef.current.abort(); } catch (e) { /* noop */ } }
    if (currentSourceRef.current) { try { currentSourceRef.current.stop(); } catch (e) { /* noop */ } currentSourceRef.current = null; }
    setVoiceState('thinking');
    setStatusText(t('efd_voice_processing'));
    botResponseRef.current = '';
    screenMarkdownRef.current = '';
    emailDraftsRef.current = [];
    attachmentsRef.current = [];
    chartsRef.current = [];
    streamDoneRef.current = false;
    audioQueueRef.current = [];
    isPlayingRef.current = false;

    let questionText = '';

    const formData = new FormData();
    formData.append('audio', audioBlob, 'audio.webm');
    formData.append('language', language);
    formData.append('history', JSON.stringify(historyRef.current));
    if (conversationRef.current) formData.append('conversation_id', conversationRef.current);

    abortControllerRef.current = new AbortController();
    try {
      const response = await fetch(`${API_BASE_URL}${apiBase}/voice`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
        signal: abortControllerRef.current.signal,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let eventType = '';

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.substring(7).trim();
          } else if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));
              if (eventType === 'transcript') {
                questionText = data.text;
                setMessages((prev) => [...prev, { role: 'user', text: data.text }]);
                consecutiveFailuresRef.current = 0;
              } else if (eventType === 'text') {
                botResponseRef.current += data.text;
                setResponseText(botResponseRef.current);
              } else if (eventType === 'audio') {
                audioQueueRef.current.push(data.audio);
                if (!isPlayingRef.current) playNextInQueue();
              } else if (eventType === 'screen') {
                // Table/detail for the chat — shown, never spoken.
                screenMarkdownRef.current = data.markdown || '';
              } else if (eventType === 'email_draft') {
                // Drafted e-mail: card in the transcript right away (the
                // spoken confirmation follows), handed to the chat with the turn.
                emailDraftsRef.current = [...emailDraftsRef.current, data];
                setMessages((prev) => [...prev, { role: 'assistant', emailDraft: data }]);
              } else if (eventType === 'attachment' && data && data.url) {
                // A file the agent produced (theScreener PDF): card in the
                // transcript, never spoken — the answer only announces it.
                attachmentsRef.current = [...attachmentsRef.current, data];
                setMessages((prev) => [...prev, { role: 'assistant', attachment: data }]);
              } else if (eventType === 'chart' && data && data.spec) {
                // A chart the agent drew: card in the transcript, never
                // spoken — the answer only says what it shows.
                chartsRef.current = [...chartsRef.current, data];
                setMessages((prev) => [...prev, { role: 'assistant', chart: data }]);
              } else if (eventType === 'done') {
                streamDoneRef.current = true;
                if (data.reason === 'empty_transcript') {
                  consecutiveFailuresRef.current += 1;
                  if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
                    voiceStoppedByUserRef.current = true;
                    setVoiceState('idle');
                    return;
                  }
                } else {
                  // Park the turn — finishTurn() moves it into the transcript
                  // once the audio has finished, so the text stays under the
                  // blob for as long as it is being read aloud.
                  pendingTurnRef.current = {
                    question: questionText,
                    answer: botResponseRef.current,
                    screen: screenMarkdownRef.current,
                    emailDrafts: emailDraftsRef.current,
                    attachments: attachmentsRef.current,
                    charts: chartsRef.current,
                  };
                }
                if (!isPlayingRef.current) {
                  finishTurn();
                  setVoiceState('idle');
                  if (!voiceStoppedByUserRef.current) {
                    setTimeout(() => startListeningRef.current && startListeningRef.current(), 300);
                  }
                }
              } else if (eventType === 'error') {
                console.error('EFD voice error:', data.message);
                setVoiceState('idle');
              }
            } catch (e) { /* non-JSON data line */ }
            eventType = '';
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('EFD voice backend error:', err);
        setStatusText(t('efd_voice_error'));
        setVoiceState('idle');
      }
    }
  }, [language, t, playNextInQueue, finishTurn]);

  const initListening = useCallback(async () => {
    setVoiceState('initializing');
    setStatusText(t('efd_voice_mic_setup'));
    voiceStoppedByUserRef.current = false;
    consecutiveFailuresRef.current = 0;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      analyserRef.current = analyser;

      startListeningRef.current = () => {
        if (voiceStoppedByUserRef.current || !mediaStreamRef.current) return;
        setVoiceState('listening');
        setStatusText(t('efd_voice_listening'));
        speechStartTimeRef.current = null;
        silenceStartTimeRef.current = null;
        audioChunksRef.current = [];

        const mimeTypes = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
        let mimeType = '';
        for (const mt of mimeTypes) { if (MediaRecorder.isTypeSupported(mt)) { mimeType = mt; break; } }

        const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
        mediaRecorderRef.current = recorder;
        recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
        recorder.onstop = () => {
          if (recordingTimeoutRef.current) { clearTimeout(recordingTimeoutRef.current); recordingTimeoutRef.current = null; }
          if (audioChunksRef.current.length > 0) {
            const blob = new Blob(audioChunksRef.current, { type: mimeType || 'audio/webm' });
            sendVoiceToBackend(blob);
          }
        };
        recorder.start(100);
        listenStartTimeRef.current = Date.now();
        // Safety net via setTimeout (keeps firing in a hidden tab, unlike rAF).
        if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);
        recordingTimeoutRef.current = setTimeout(() => {
          if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
          }
        }, MAX_RECORDING_MS);

        const dataArray = new Float32Array(analyser.frequencyBinCount);
        const vadLoop = () => {
          if (voiceStoppedByUserRef.current) return;
          analyser.getFloatTimeDomainData(dataArray);
          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) sum += dataArray[i] * dataArray[i];
          const rms = Math.sqrt(sum / dataArray.length);
          setAudioLevel(rms);
          const now = Date.now();
          if (!speechStartTimeRef.current && listenStartTimeRef.current
              && now - listenStartTimeRef.current >= NO_SPEECH_TIMEOUT_MS) {
            if (recorder.state === 'recording') recorder.stop();
            return;
          }
          if (rms > VAD_THRESHOLD) {
            silenceStartTimeRef.current = null;
            if (!speechStartTimeRef.current) speechStartTimeRef.current = now;
          } else if (speechStartTimeRef.current) {
            if (now - speechStartTimeRef.current >= MIN_SPEECH_MS) {
              if (!silenceStartTimeRef.current) {
                silenceStartTimeRef.current = now;
              } else if (now - silenceStartTimeRef.current >= SILENCE_MS) {
                if (recorder.state === 'recording') recorder.stop();
                return;
              }
            }
          }
          vadFrameIdRef.current = requestAnimationFrame(vadLoop);
        };
        vadFrameIdRef.current = requestAnimationFrame(vadLoop);
      };

      startListeningRef.current();
    } catch (err) {
      console.error('Microphone error:', err);
      setVoiceState('idle');
      setStatusText(t('efd_voice_mic_denied'));
    }
  }, [sendVoiceToBackend, t]);

  const stopVoice = useCallback(() => {
    voiceStoppedByUserRef.current = true;
    if (vadFrameIdRef.current) cancelAnimationFrame(vadFrameIdRef.current);
    if (recordingTimeoutRef.current) { clearTimeout(recordingTimeoutRef.current); recordingTimeoutRef.current = null; }
    if (abortControllerRef.current) abortControllerRef.current.abort();
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }
    if (mediaStreamRef.current) { mediaStreamRef.current.getTracks().forEach((tr) => tr.stop()); mediaStreamRef.current = null; }
    if (audioContextRef.current) { try { audioContextRef.current.close(); } catch (e) {} audioContextRef.current = null; }
    if (ttsAudioContextRef.current) { try { ttsAudioContextRef.current.close(); } catch (e) {} ttsAudioContextRef.current = null; }
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    setVoiceState('idle');
  }, []);

  /** Barge-in: tap while the agent thinks or speaks to interrupt and talk. */
  const stopAndSpeakAgain = useCallback(() => {
    if (voiceState !== 'thinking' && voiceState !== 'speaking') return;
    if (abortControllerRef.current) abortControllerRef.current.abort();
    if (currentSourceRef.current) { try { currentSourceRef.current.stop(); } catch (e) {} currentSourceRef.current = null; }
    if (ttsAudioContextRef.current) { try { ttsAudioContextRef.current.close(); } catch (e) {} ttsAudioContextRef.current = null; }
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    streamDoneRef.current = false;
    finishTurn();   // keep what was already answered, even if cut off
    startListening();
  }, [voiceState, startListening, finishTurn]);

  // The transcript is a short scroll box (max 32% height): follow the newest
  // entry so the running conversation stays visible while the agent speaks.
  useEffect(() => {
    const el = transcriptRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, responseText]);

  const handleClose = useCallback(() => {
    finishTurn();   // an answer cut short by closing still belongs in the chat
    stopVoice();
    if (onClose) onClose();
  }, [finishTurn, stopVoice, onClose]);

  useEffect(() => {
    initListening();
    return () => stopVoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const interactive = voiceState === 'thinking' || voiceState === 'speaking';

  return (
    <div className="av-overlay">
      <IconButton onClick={handleClose} size="small" className="av-close"
        sx={{ position: 'absolute', top: 10, right: 10, color: 'text.secondary', bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
        <CloseIcon />
      </IconButton>

      <div className="av-body" onClick={interactive ? stopAndSpeakAgain : undefined} style={{ cursor: interactive ? 'pointer' : 'default' }}>
        <div className="av-blob-wrap">
          <div className={`av-blob av-${voiceState}`} style={{ transform: `scale(${1 + audioLevel * 0.3})` }}>
            <div className="av-blob-inner" />
            <div className="av-blob-ring r1" />
            <div className="av-blob-ring r2" />
          </div>
        </div>

        <div className="av-status">{statusText}</div>
        {responseText && <div className="av-response">{responseText}</div>}
        {interactive && <div className="av-hint">{t('efd_voice_interrupt')}</div>}
      </div>

      {messages.length > 0 && (
        <div className="av-transcript" ref={transcriptRef}>
          {messages.map((m, i) => (
            <div key={i} className={`av-msg av-msg-${m.role}`}>
              <span className="av-role">{m.role === 'user' ? t('are_voice_you') : t('are_voice_assistant')}</span>
              {m.emailDraft
                ? <EmailDraftCard draft={m.emailDraft} openLabel={t('open_email')} />
                : m.attachment
                  ? <AttachmentCard attachment={m.attachment} openLabel={t('efd_attachment_open')} />
                  : m.chart
                    ? <ChartCard spec={m.chart.spec} />
                    : <span className="av-text">{m.text}</span>}
            </div>
          ))}
        </div>
      )}

      <AreDisclaimer sx={{ flexShrink: 0, justifyContent: 'center', textAlign: 'center', pb: 1 }} />
    </div>
  );
}

export default EfdVoiceDialog;
